import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useBackgroundJobs } from '../../contexts/BackgroundJobsContext';
import { UploadBacklogFeeDialog } from '../bankfeed/UploadBacklogFeeDialog';
import { notifyUploadCancelled } from '../../utils/uploadCancelToast';
import { isIOSNative } from '../../utils/nativeBridge';
import {
  claimBacklogModal,
  releaseBacklogModal,
  subscribeBacklogModal,
  getBacklogModalHolder,
} from './backlogModalMutex';
// iter1238 Phase 8.9 — `isFreshEnoughForModal` no longer needed; the
// modal opens only on explicit user request (openRequested flag).

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * GlobalUploadBacklogModalProvider - iter1230
 * ---------------------------------------------------------------
 * Watches BackgroundJobsContext for statement-upload jobs that are
 * paused at `awaiting_confirmation` and surfaces the backlog-fee
 * confirmation modal on EVERY page. Handles both cases:
 *   - Real charge → POSTs confirm, waits for success, on failure keeps
 *     the job paused so the user can retry from Bank Accounts / here.
 *   - Fully-free (V2 lifetime allowance / admin waiver) → same confirm
 *     endpoint but the backend skips the charge step.
 *
 * On successful confirm the job flips to `processing` and the global
 * progress BANNER (`GlobalUploadProgressBadge`) takes over the UI.
 *
 * This provider must be mounted inside `BackgroundJobsProvider`, which
 * DashboardLayout already provides via App.js.
 */
const GlobalUploadBacklogModalProviderInner = () => {
  const { jobs, removeJob, clearReviewRequest } = useBackgroundJobs();
  const [busy, setBusy] = useState(false);
  const [openJob, setOpenJob] = useState(null);
  // Jobs the user explicitly deferred this session ("Try later").
  // Keyed by jobId. This survives navigation but resets on hard reload
  // so returning users see the pending decision again.
  const [deferred, setDeferred] = useState(() => new Set());
  // iter1238 Phase 8.13f · Jobs the user has ALREADY actioned this
  // session (Pay / Skip / Cancel). Suppresses modal re-open during the
  // 2-5 s window between the confirm/cancel API returning and the
  // BackgroundJobsContext polling picking up the new status. Without
  // this, the useEffect below sees `status === 'awaiting_confirmation'`
  // still in the `jobs` array and instantly re-opens the modal — the
  // "modal won't die" bug users have reported repeatedly.
  const [handledIds, setHandledIds] = useState(() => new Set());

  // Feb 2026 · Cross-provider modal mutex — GlobalBacklogModalProvider
  // (Mono/Plaid) can also surface a backlog dialog. When both were
  // pending we used to stack two visually identical dialogs on top of
  // each other ("the backlog modal is duplicated"). This mutex claims
  // the modal channel while an upload dialog is open, and releases it
  // on close so the sibling provider can take over next tick.
  const [mutexHolder, setMutexHolder] = useState(() => getBacklogModalHolder());
  useEffect(() => subscribeBacklogModal(setMutexHolder), []);
  useEffect(() => {
    if (openJob) {
      const ok = claimBacklogModal('upload');
      if (!ok) return undefined; // openbanking provider is holding
      return () => releaseBacklogModal('upload');
    }
    return undefined;
  }, [openJob]);

  // iter1238 Phase 8.10 · P0.3 — the fee modal MUST auto-open the
  // moment parsing completes and a real backlog fee is waiting,
  // on ANY page and even after logout+login. Skip conditions:
  //  * stale preview_stage=true jobs (killed in Phase 8.8)
  //  * jobs the user tapped "Try later" on this session
  //    (unless the user explicitly re-requested review from the pill,
  //     signalled by openRequested=true)
  //  * jobs already actioned this session (handledIds)
  //  * jobs in a user-terminal state (cancelled/failed) — belt-and-
  //    braces, the polling layer shouldn't hand these to us anyway
  useEffect(() => {
    if (busy) return;
    const awaiting = (jobs || []).find((j) => (
      (j.status === 'awaiting_confirmation' || j.status === 'pending_payment')
      && !!j.billing
      && !j.billing.preview_stage
      && !handledIds.has(j.jobId)
      && (j.openRequested === true || !deferred.has(j.jobId))
    ));
    if (awaiting && (!openJob || openJob.jobId !== awaiting.jobId)) {
      // If user explicitly re-requested this via the pill, drop it from
      // the deferred set so subsequent auto-opens (e.g. tab refocus)
      // don't re-hide it.
      if (awaiting.openRequested && deferred.has(awaiting.jobId)) {
        setDeferred((prev) => {
          const next = new Set(prev);
          next.delete(awaiting.jobId);
          return next;
        });
      }
      setOpenJob({
        jobId: awaiting.jobId,
        filename: awaiting.filename,
        billing: awaiting.billing,
        bridgePreview: awaiting.bridgePreview || null,
      });
    }
    if (!awaiting && openJob) {
      setOpenJob(null);
    }
  }, [jobs, busy, deferred, handledIds, openJob]);

  // iter1238 Phase 8.13f · Auto-cleanup for `handledIds`: once a job
  // either leaves the `awaiting_confirmation`/`pending_payment` states
  // (backend caught up) OR disappears from the jobs list entirely
  // (cancelled/removed), drop it from handledIds so a future re-upload
  // with a fresh jobId isn't accidentally suppressed. This is a no-op
  // when there's nothing to clean.
  useEffect(() => {
    if (handledIds.size === 0) return;
    const stillPending = new Set(
      (jobs || [])
        .filter((j) => j.status === 'awaiting_confirmation' || j.status === 'pending_payment')
        .map((j) => j.jobId)
    );
    let changed = false;
    const next = new Set(handledIds);
    handledIds.forEach((id) => {
      if (!stillPending.has(id)) {
        next.delete(id);
        changed = true;
      }
    });
    if (changed) setHandledIds(next);
  }, [jobs, handledIds]);

  const markHandled = useCallback((jobId) => {
    if (!jobId) return;
    setHandledIds((prev) => {
      const next = new Set(prev);
      next.add(jobId);
      return next;
    });
  }, []);

  const handleConfirm = useCallback(async (mode) => {
    if (!openJob) return;
    const jobId = openJob.jobId;
    setBusy(true);
    // Close the modal optimistically and mark the job as handled so
    // the auto-open effect above doesn't re-surface it while the
    // backend polling catches up.
    markHandled(jobId);
    setOpenJob(null);
    try {
      const { data } = await axios.post(
        `${API}/bank-feeds/upload-jobs/${jobId}/confirm`,
        { mode }
      );
      if (data.success) {
        toast.success(data.message || 'Upload resumed. Watch the progress banner for updates.');
        if (clearReviewRequest) clearReviewRequest(jobId);
        // The polling loop inside BackgroundJobsContext will pick up
        // the new processing status and flip the banner over. The
        // handled-id cleanup effect drops it once the status moves.
      } else if (data.declined) {
        toast.error(
          data.message || 'Payment declined. Try again from Bank Accounts or update your card.',
          { duration: 10000 }
        );
        // Payment declined — unhandle so the user can retry from the
        // modal without a page refresh.
        setHandledIds((prev) => {
          const next = new Set(prev);
          next.delete(jobId);
          return next;
        });
      } else {
        toast.error(data.message || 'Could not confirm the upload. Please try again.');
        setHandledIds((prev) => {
          const next = new Set(prev);
          next.delete(jobId);
          return next;
        });
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Backlog confirm failed: ${detail}`);
      setHandledIds((prev) => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    } finally {
      setBusy(false);
    }
  }, [openJob, clearReviewRequest, markHandled]);

  const handleCancel = useCallback(async (cancelUpload) => {
    if (!openJob) return;
    const jobId = openJob.jobId;
    const filename = openJob.filename;
    // Close + mark handled synchronously so the useEffect can't
    // race and re-open the modal on the next tick.
    markHandled(jobId);
    setOpenJob(null);
    if (cancelUpload) {
      // Hard cancel - delete the job on the server.
      try {
        await axios.post(`${API}/bank-feeds/upload-jobs/${jobId}/cancel`);
      } catch (_) { /* best-effort */ }
      try { removeJob && removeJob(jobId); } catch (_) { /* noop */ }
      notifyUploadCancelled(jobId, filename);
    } else {
      // Soft dismiss - clear the openRequested flag and remember the
      // deferral so the same job doesn't spontaneously re-open.
      if (clearReviewRequest) clearReviewRequest(jobId);
      setDeferred((prev) => {
        const next = new Set(prev);
        next.add(jobId);
        return next;
      });
    }
  }, [openJob, removeJob, clearReviewRequest, markHandled]);

  return (
    <UploadBacklogFeeDialog
      open={!!openJob && mutexHolder === 'upload'}
      job={openJob}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      busy={busy}
    />
  );
};

// iOS App Store build: fee-confirmation UI must never render in-app.
const GlobalUploadBacklogModalProvider = () =>
  isIOSNative() ? null : <GlobalUploadBacklogModalProviderInner />;

export default GlobalUploadBacklogModalProvider;
